import * as Sentry from '@sentry/react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { UserRole } from '@/constants/users';
import type { ReactNode } from 'react';
import { fetchWithRetry } from '@/utils/retry';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 認証状態の復元時に refresh の応答を待つ上限。
 *
 * isLoaded は initializeAuth() の完了に紐づいているため、この往復が settle しないと
 * 復元中のまま固定される(ProtectedRoute はローディング表示のまま、
 * LoginForm は会員種別を判定できず待ち続ける)。
 * 素の fetch は「TCPは繋がるが応答が返らない」状態では解決しないため、上限を設けて
 * 必ず失敗として決着させる。失敗すればログイン画面に戻るので、少なくとも操作可能な状態は保たれる。
 *
 * utils/retry.ts の既定(60秒)より短くしているのは、この往復だけが画面全体をブロックするため。
 * 逆に、セッション中の refresh(ensureValidToken 経由)にこの上限を適用してはいけない。
 * 遅いだけで成功する refresh を打ち切ると、操作中のユーザーを強制ログアウトさせてしまう。
 */
const REFRESH_TIMEOUT_MS = 15000;

export type { UserRole };

interface AuthContextType {
  jwt: string | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  isLoaded: boolean;
  login: (jwt: string, expiresAt: number, role: UserRole) => Promise<void>;
  logout: () => void;
  refreshToken: (jwt: string, timeoutMs?: number) => Promise<boolean>;
  ensureValidToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [jwt, setJwt] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);

  // refresh処理中のPromiseをキャッシュ（同時実行を防ぐ）
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);

  const [isLoaded, setIsLoaded] = useState(false);

  // 初期化時にlocalStorageからトークンを復元
  useEffect(() => {
    const initializeAuth = async () => {
      const storedJwt = localStorage.getItem('jwt');
      const storedExpiresAt = localStorage.getItem('token_expires_at');
      const storedRole = localStorage.getItem('user_role') as UserRole | null;

      if (storedJwt && storedExpiresAt) {
        const expiresAt = parseInt(storedExpiresAt, 10);
        const now = Math.floor(Date.now() / 1000);

        // トークンが有効期限内の場合のみ復元
        if (expiresAt > now) {
          setJwt(storedJwt);
          setRole(storedRole);
        } else {
          console.log('Access token expired, attempting refresh...');

          // refreshを実行（awaitを付けて完了を待つ）
          const success = await refreshToken(storedJwt, REFRESH_TIMEOUT_MS);
          if (!success) {
            console.error('Token refresh failed during initialization');
            // refreshToken内部でlogout()が呼ばれるため、ここでは追加処理不要
          }
        }
      }
    };

    // 期限切れトークンの refresh はネットワーク往復を伴うため、完了を待ってから isLoaded を立てる。
    // 先に立てると復元中の状態が「復元済み・未ログイン」と区別できず、
    // ProtectedRoute が refresh 成功前にログイン画面へ飛ばしたり、
    // useSuiteLoungeSessionReset が会員種別を判定できないまま先に進んでしまう。
    void initializeAuth().finally(() => setIsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // resetSession(useSuiteLoungeSessionReset)など、これらの関数を依存配列に持つ
  // 呼び出し側の effect が AuthProvider の再レンダリングごとに再実行されないよう memo 化する
  const login = useCallback(
    async (jwt: string, expiresAt: number, userRole: UserRole) => {
      setJwt(jwt);
      setRole(userRole);

      // localStorageに保存
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('token_expires_at', expiresAt.toString());
      localStorage.setItem('user_role', userRole);
    },
    [],
  );

  const logout = useCallback(() => {
    setJwt(null);
    setRole(null);

    // localStorageからクリア
    localStorage.removeItem('jwt');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user_role');
  }, []);

  const refreshToken = useCallback(
    async (storedJwt: string, timeoutMs?: number): Promise<boolean> => {
      // 既にリクエスト中の場合は、そのPromiseを返す
      if (refreshPromiseRef.current) {
        console.log(
          'Refresh already in progress, waiting for it to complete...',
        );
        return refreshPromiseRef.current;
      }

      // 新しいrefresh処理を開始
      const refreshPromise = (async () => {
        try {
          console.log('Attempting to refresh access token...');
          // 上限は fetch だけに掛ける(fetchWithRetry)。
          // 打ち切った後に届いた応答は race の外にあり、login()/logout() を呼ぶことはない。
          const response = await fetchWithRetry(
            () =>
              fetch(`${BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${storedJwt}`,
                },
              }),
            { enabled: false, timeoutMs },
          );

          if (!response.ok) {
            console.error('Token refresh failed with status:', response.status);
            // 500番台のサーバーエラーはSentryに送信
            if (response.status >= 500) {
              Sentry.captureMessage('Token refresh failed', {
                level: 'error',
                tags: {
                  statusCode: response.status,
                  errorType: 'auth_refresh_failed',
                },
              });
            }
            logout();
            return false;
          }

          const data = await response.json();

          console.log('Token refresh successful!');

          // 新しいtokenを保存
          await login(data.data.jwt, data.data.expires_at, data.data.role);
          return true;
        } catch (error) {
          console.error('Token refresh error:', error);
          // 予期しないエラーをSentryに送信
          Sentry.captureException(error, {
            tags: {
              errorType: 'auth_refresh_exception',
            },
          });
          logout();
          return false;
        } finally {
          // 処理完了後、キャッシュをクリア
          refreshPromiseRef.current = null;
        }
      })();

      // Promiseをキャッシュ
      refreshPromiseRef.current = refreshPromise;
      return refreshPromise;
    },
    [login, logout],
  );

  /**
   * 有効なjwtを保証する
   * - jwtが期限切れまたは期限間近の場合: refreshを試みる
   * - 成功時: 有効なjwtを返す
   * - 失敗時: nullを返す
   */
  const ensureValidToken = useCallback(async (): Promise<string | null> => {
    if (!jwt) return null;

    // refresh処理が既に実行中の場合は、その完了を待つ
    if (refreshPromiseRef.current) {
      console.log('Refresh in progress, waiting...');
      await refreshPromiseRef.current;
      return localStorage.getItem('jwt');
    }

    // jwtがある場合は有効期限をチェック
    const expiresAt = localStorage.getItem('token_expires_at');
    if (expiresAt) {
      const expiresAtNum = parseInt(expiresAt, 10);
      const now = Math.floor(Date.now() / 1000);

      // 既に期限切れ、または1分未満の場合はrefresh
      if (expiresAtNum <= now || expiresAtNum - now < 60) {
        console.log('JWT expired or expiring soon, refreshing...');
        const success = await refreshToken(jwt);
        if (success) {
          return localStorage.getItem('jwt');
        }
        return null;
      }

      // まだ有効
      return jwt;
    } else {
      // jwtはあるがexpires_atが無い場合（異常状態）
      console.log('No token expiry found but has jwt, attempting refresh...');
      const success = await refreshToken(jwt);
      if (success) {
        return localStorage.getItem('jwt');
      }
      return null;
    }
  }, [jwt, refreshToken]);

  const isAuthenticated = !!jwt;

  const value: AuthContextType = useMemo(
    () => ({
      jwt,
      role,
      isAuthenticated,
      isLoaded,
      login,
      logout,
      refreshToken,
      ensureValidToken,
    }),
    [
      jwt,
      role,
      isAuthenticated,
      isLoaded,
      login,
      logout,
      refreshToken,
      ensureValidToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
